import React, { useState, useEffect } from 'react';
import { Search, AlertCircle, Loader2, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface StockMovement {
  id: number;
  tipo: string;
  variante_nombre: string;
  cantidad: number;
  local_origen_nombre: string | null;
  local_destino_nombre: string | null;
  fecha: string;
  motivo: string;
}

interface StockMovementsProps {
  companySchema: string;
}

export const StockMovements: React.FC<StockMovementsProps> = ({ companySchema }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [selectedType, setSelectedType] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Last 30 days by default
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });

  useEffect(() => {
    fetchMovements();
  }, [startDate, endDate]);

  const fetchMovements = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching movements with dates:', { startDate, endDate });

      const { data, error: movementsError } = await supabase
        .schema(companySchema)
        .from('movimiento_de_stock')
        .select(`
          id,
          tipo,
          cantidad,
          motivo,
          fecha,
          variantes!inner (
            nombre
          ),
          origen:locales!movimiento_de_stock_local_id_origen_fkey (
            nombre
          ),
          destino:locales!movimiento_de_stock_local_id_destino_fkey (
            nombre
          )
        `)
        .gte('fecha', startDate)
        .lte('fecha', endDate)
        .order('fecha', { ascending: false });

      console.log('Raw movements data:', data);
      console.log('Query error:', movementsError);

      if (movementsError) throw movementsError;

      const formattedMovements = data.map(movement => ({
        id: movement.id,
        tipo: movement.tipo,
        variante_nombre: movement.variantes.nombre,
        cantidad: movement.cantidad,
        local_origen_nombre: movement.origen?.nombre || 'N/A',
        local_destino_nombre: movement.destino?.nombre || 'N/A',
        fecha: new Date(movement.fecha).toLocaleString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }),
        motivo: movement.motivo
      }));

      console.log('Formatted movements:', formattedMovements);
      setMovements(formattedMovements);
    } catch (err) {
      console.error('Error fetching movements:', err);
      setError(err instanceof Error ? err.message : 'Error loading movements');
    } finally {
      setLoading(false);
    }
  };

  const filteredMovements = movements.filter(movement => {
    const searchTermLower = searchTerm.toLowerCase();
    const matchesType = selectedType ? movement.tipo === selectedType : true;
    return (
      matchesType && (
        movement.variante_nombre.toLowerCase().includes(searchTermLower) ||
        movement.local_origen_nombre.toLowerCase().includes(searchTermLower) ||
        movement.local_destino_nombre.toLowerCase().includes(searchTermLower) ||
        movement.motivo.toLowerCase().includes(searchTermLower)
      )
    );
  });

  const getMovementTypeStyle = (tipo: string) => {
    switch (tipo) {
      case 'entrada':
        return 'bg-green-50 text-green-700';
      case 'salida':
        return 'bg-red-50 text-red-700';
      case 'transferencia':
        return 'bg-blue-50 text-blue-700';
      case 'ajuste':
        return 'bg-yellow-50 text-yellow-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  if (loading && !movements.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los tipos</option>
              <option value="entrada">Entrada</option>
              <option value="salida">Salida</option>
              <option value="transferencia">Transferencia</option>
              <option value="ajuste">Ajuste</option>
            </select>
          </div>

          {/* Search Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por variante o motivo..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Desde
            </label>
            <div className="relative">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Calendar className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hasta
            </label>
            <div className="relative">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Calendar className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>
        </div>
      </div>

      {/* Movements Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Variante
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cantidad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Local Origen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Local Destino
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Motivo
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMovements.map((movement) => (
                <tr key={movement.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMovementTypeStyle(movement.tipo)}`}>
                      {movement.tipo}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {movement.variante_nombre}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {movement.cantidad}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {movement.local_origen_nombre}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {movement.local_destino_nombre}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {movement.fecha}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {movement.motivo}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};